import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { buzz, tapMedium } from '../lib/haptics';

/* Warm, on-brand confetti palette */
const CONFETTI_COLORS = [
  'oklch(66% 0.2 45)',   // flame
  'oklch(80% 0.15 85)',  // gold
  'oklch(74% 0.16 66)',  // amber
  'oklch(64% 0.16 150)', // success green
  'oklch(52% 0.17 52)',  // terracotta
  'oklch(92% 0.04 75)',  // cream
];

interface Piece {
  id: number;
  side: 'l' | 'r'; // which cannon
  top: number;     // launch height (% from top)
  edge: number;    // % offset from the firing edge
  vx: string;      // net horizontal travel (inward), in vw
  peak: string;    // apex height reached (negative vh)
  fall: string;    // final downward travel (positive vh)
  bg: string;
  cx: string;    // small flutter amplitude
  cr: string;    // total Z spin
  cflip: string; // total X tumble
  cd: string;    // duration
  delay: string;
  round: boolean;
  w: number;
  h: number;
}

interface Toast {
  id: number;
  icon: string;
  title: string;
  sub?: string;
}

interface CelebrationApi {
  /** Grand finale: exaggerated two-beat "ta-da" + an achievement toast. For hitting the daily goal. */
  celebrate: (achievement?: { icon: string; title: string; sub?: string }) => void;
  /** Two-beat "ta-da" celebration with synced haptics. The standard reward for completing a chore. */
  tada: () => void;
  /** Small confetti puff + light haptic. */
  confetti: (count?: number) => void;
}

const CelebrationContext = createContext<CelebrationApi | null>(null);

let pieceSeq = 0;
let toastSeq = 0;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

export function CelebrationProvider({ children }: { children: ReactNode }) {
  const [pieces, setPieces] = useState<Piece[]>([]);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  const spawn = useCallback((count: number, power = 1) => {
    if (prefersReducedMotion()) return;
    const sizeScale = 0.85 + power * 0.45; // bigger pieces at higher power
    // Two poppers, one per side, around 70–80% up the screen — with a guaranteed
    // offset between the sides so they never fire from the same height (more natural).
    const baseTop = 20 + Math.random() * 18;             // top 20–28% == 72–80% up
    const offset = 3 + Math.random() * 10;               // 3–8% apart
    const leftHigher = Math.random() < 0.5;
    const leftTop = leftHigher ? baseTop : baseTop + offset;
    const rightTop = leftHigher ? baseTop + offset : baseTop;
    const batch: Piece[] = Array.from({ length: count }, (_, idx) => {
      const id = pieceSeq++;
      const side: 'l' | 'r' = idx % 2 === 0 ? 'l' : 'r';
      const dir = side === 'l' ? 1 : -1; // fire inward
      const reach = (28 + Math.random() * 62) * (0.85 + power * 0.3); // vw travelled inward
      return {
        id,
        side,
        top: (side === 'l' ? leftTop : rightTop) + (Math.random() * 2 - 1) * 3,
        edge: Math.random() * 4,
        vx: `${dir * reach}vw`,
        peak: `${-(16 + Math.random() * 32)}vh`,           // launched upward
        fall: `${78 + Math.random() * 40}vh`,              // then drops off-screen
        bg: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)]!,
        cx: `${(Math.random() < 0.5 ? -1 : 1) * (8 + Math.random() * 20)}px`, // subtle flutter
        cr: `${dir * (360 + Math.random() * 720)}deg`,     // 1–3 spins
        cflip: `${(Math.random() < 0.5 ? -1 : 1) * (360 + Math.random() * 540)}deg`,
        cd: `${1.8 + Math.random() * 1.3}s`,
        delay: `${Math.random() * 0.07}s`,                 // near-instant pop
        round: Math.random() < 0.45,
        w: Math.round(9 * sizeScale),
        h: Math.round(14 * sizeScale),
      };
    });
    setPieces((p) => [...p, ...batch]);
    const t = setTimeout(() => {
      const ids = new Set(batch.map((b) => b.id));
      setPieces((p) => p.filter((piece) => !ids.has(piece.id)));
    }, 4000);
    timers.current.push(t);
  }, []);

  const confetti = useCallback((count = 16) => {
    tapMedium();
    spawn(count);
  }, [spawn]);

  // The two-beat "ta-da": a quick pop, then a beat later a bigger, longer-buzzed
  // explosion. `big` cranks everything up for the daily-goal finale.
  const runTada = useCallback((opts?: { big?: boolean; achievement?: { icon: string; title: string; sub?: string } }) => {
    const big = opts?.big ?? false;

    // "Ta" — a snappy pop with a short buzz.
    buzz(big ? 50 : 40);
    spawn(big ? 40 : 32, big ? 1.4 : 1.2);

    // "...DA!" — a beat later, a dramatic burst with a longer buzz.
    const t1 = setTimeout(() => {
      buzz(big ? 230 : 130);
      spawn(big ? 110 : 76, big ? 2.2 : 1.8);
      if (opts?.achievement) {
        const id = toastSeq++;
        setToasts((t) => [...t, { id, ...opts.achievement! }]);
        const t = setTimeout(() => setToasts((cur) => cur.filter((x) => x.id !== id)), 3500);
        timers.current.push(t);
      }
    }, 250);
    timers.current.push(t1);
  }, [spawn]);

  const tada = useCallback(() => runTada(), [runTada]);
  const celebrate = useCallback<CelebrationApi['celebrate']>((achievement) => runTada({ big: true, achievement }), [runTada]);

  return (
    <CelebrationContext.Provider value={{ celebrate, tada, confetti }}>
      {children}
      {createPortal(
        <>
          {pieces.length > 0 && (
            <div className="confetti-layer" aria-hidden="true">
              {pieces.map((p) => (
                <span
                  key={p.id}
                  className="confetti-piece"
                  style={{
                    top: `${p.top}%`,
                    [p.side === 'l' ? 'left' : 'right']: `${p.edge}%`,
                    // CSS custom props drive the ballistic launch keyframe
                    ['--vx' as string]: p.vx,
                    ['--peak' as string]: p.peak,
                    ['--fall' as string]: p.fall,
                    ['--cd' as string]: p.cd,
                    animationDelay: p.delay,
                  }}
                >
                  <span
                    className="confetti-bit"
                    style={{
                      width: p.w,
                      height: p.h,
                      background: p.bg,
                      borderRadius: p.round ? '50%' : '2px',
                      // CSS custom props drive the sway/tumble keyframe
                      ['--cx' as string]: p.cx,
                      ['--cr' as string]: p.cr,
                      ['--cflip' as string]: p.cflip,
                      ['--cd' as string]: p.cd,
                      animationDelay: p.delay,
                    }}
                  />
                </span>
              ))}
            </div>
          )}
          {toasts.map((t) => (
            <div key={t.id} className="achievement-toast" role="status">
              <span className="medallion" style={{ width: 44, height: 44, fontSize: '1.25rem' }}>
                <span className="medallion-shine" />
                <ion-icon name={t.icon} />
              </span>
              <div style={{ minWidth: 0 }}>
                <div className="achievement-toast-title">{t.title}</div>
                {t.sub && <div className="achievement-toast-sub">{t.sub}</div>}
              </div>
            </div>
          ))}
        </>,
        document.body,
      )}
    </CelebrationContext.Provider>
  );
}

export function useCelebration(): CelebrationApi {
  const ctx = useContext(CelebrationContext);
  if (!ctx) {
    // Safe no-op fallback so components never crash outside the provider.
    return {
      celebrate: () => { },
      tada: () => { },
      confetti: () => { },
    };
  }
  return ctx;
}
